import { format } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';

import { CalendarIcon } from '@heroicons/react/outline';
import { TextInput } from '@yoast/ui-library';
import { default as classNames, default as cx } from 'classnames';
import type {
  AnalysisBase,
  AnalysisIdOrLatest,
} from '../../api/generated/insights.schemas';
import AnalysisCalendarPicker from './AnalysisCalendarPicker';

interface AnalysisDateSelectProps {
  analyses: AnalysisBase[];
  selectedAnalysisId: AnalysisIdOrLatest;
  onAnalysisSelect: (analysisId: AnalysisIdOrLatest) => void;
  isLoading?: boolean;
  disabled?: boolean;
  id?: string;
}

const AnalysisDateSelect = ({
  analyses,
  selectedAnalysisId,
  onAnalysisSelect,
  isLoading = false,
  disabled = false,
  id = 'analysis-date-select',
}: AnalysisDateSelectProps) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [localSelectedId, setLocalSelectedId] =
    useState<AnalysisIdOrLatest>('latest');
  const selectRef = useRef<HTMLDivElement>(null);

  // Safely derive a local Date object from the UTC ISO string using only the date portion (YYYY-MM-DD)
  // This avoids off-by-one day issues due to timezone conversions when formatting for display.
  const getLocalDateFromCreatedAt = (createdAt?: string): Date | null => {
    if (!createdAt) return null;
    const ymd = createdAt.slice(0, 10); // 'YYYY-MM-DD'
    const [yearStr, monthStr, dayStr] = ymd.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (
      Number.isNaN(year) ||
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      return null;
    }
    // Construct as local date (not UTC) so that formatting displays the intended calendar date
    return new Date(year, month - 1, day);
  };

  // Return a numeric timestamp for an analysis; use -Infinity for invalid/missing dates so comparisons work.
  const getAnalysisTimestamp = (analysis: AnalysisBase): number => {
    const d = getLocalDateFromCreatedAt(analysis.createdAt);
    return d ? d.getTime() : -Infinity;
  };

  // Auto-select most recent analysis if 'latest', empty string, or no selection
  useEffect(() => {
    if (
      (selectedAnalysisId === 'latest' || !selectedAnalysisId) &&
      analyses.length > 0 &&
      !isLoading
    ) {
      const mostRecentAnalysis = analyses.reduce((latest, analysis) =>
        getAnalysisTimestamp(analysis) > getAnalysisTimestamp(latest)
          ? analysis
          : latest,
      );
      onAnalysisSelect(mostRecentAnalysis.id!);
    }
  }, [analyses, selectedAnalysisId, onAnalysisSelect, isLoading]);

  // Get the effective selected analysis ID (resolve 'latest' to actual ID)
  const effectiveSelectedId = useMemo(() => {
    if (selectedAnalysisId === 'latest' || !selectedAnalysisId) {
      if (analyses.length > 0) {
        const mostRecentAnalysis = analyses.reduce((latest, analysis) =>
          getAnalysisTimestamp(analysis) > getAnalysisTimestamp(latest)
            ? analysis
            : latest,
        );
        return mostRecentAnalysis.id;
      }
      return 'latest';
    }
    return selectedAnalysisId;
  }, [selectedAnalysisId, analyses, getAnalysisTimestamp]);

  // Keep a local selected id in sync with effective selection for snappy UI updates
  useEffect(() => {
    setLocalSelectedId(effectiveSelectedId!);
  }, [effectiveSelectedId]);

  // Find the selected analysis to display its date
  const selectedAnalysis = analyses.find(
    (a) => String(a.id) === String(localSelectedId),
  );

  // Determine if selected analysis is the most recent
  const mostRecentAnalysisId =
    analyses.length > 0
      ? String(
          analyses.reduce((latest, analysis) =>
            getAnalysisTimestamp(analysis) > getAnalysisTimestamp(latest)
              ? analysis
              : latest,
          ).id,
        )
      : '';

  const selectedLabel = (() => {
    if (selectedAnalysis) {
      const localDate = getLocalDateFromCreatedAt(selectedAnalysis.createdAt);
      const dateLabel = localDate
        ? format(localDate, 'MMMM d, yyyy')
        : // Fallback if parsing fails
          selectedAnalysis.createdAt
          ? selectedAnalysis.createdAt.slice(0, 10)
          : 'Unknown date';
      const recencyLabel =
        String(selectedAnalysis.id) === mostRecentAnalysisId
          ? '(latest)'
          : '(older)';
      return `${dateLabel} ${recencyLabel}`;
    }
    return analyses.length > 0 ? 'Select date...' : 'No analyses available';
  })();

  // Handle clicking outside to close calendar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsCalendarOpen(false);
      }
    };

    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);

  const handleSelectClick = () => {
    if (!disabled && !isLoading) {
      setIsCalendarOpen(!isCalendarOpen);
    }
  };

  const handleAnalysisSelect = (analysisId: AnalysisIdOrLatest) => {
    // Update local selection immediately for instant UI feedback
    setLocalSelectedId(analysisId);
    onAnalysisSelect(analysisId);
    setIsCalendarOpen(false);
  };

  const handleCalendarClose = () => {
    setIsCalendarOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelectClick();
    } else if (event.key === 'Escape') {
      setIsCalendarOpen(false);
    }
  };

  return (
    <div ref={selectRef} className="yst-relative">
      {/* Custom Select-like Button with Calendar Icon */}
      <div className="yst-relative">
        <TextInput
          id={id}
          onClick={handleSelectClick}
          onKeyDown={handleKeyDown}
          onChange={() => {}} // No-op onChange to satisfy React
          disabled={disabled || isLoading}
          value={selectedLabel}
          aria-expanded={isCalendarOpen}
          aria-haspopup="true"
          aria-label="Select analysis date"
          className={cx(
            // Add padding for icon space and pointer cursor
            'yst-pr-10',
            {
              // Use cursor type default to match ui-library
              'yst-cursor-default': !disabled && !isLoading,
            },
          )}
        />
        {/* Calendar Icon */}
        {/* If disabled, grey icon */}
        <CalendarIcon
          className={classNames(
            'yst-pointer-events-none yst-absolute yst-right-3 yst-top-1/2 yst--translate-y-1/2 yst-transform',
            {
              'yst-text-gray-400': disabled || isLoading,
            },
          )}
        />
      </div>
      {/* Calendar Picker Dropdown */}
      <AnalysisCalendarPicker
        analyses={analyses}
        selectedAnalysisId={localSelectedId}
        onAnalysisSelect={handleAnalysisSelect}
        isLoading={isLoading}
        isOpen={isCalendarOpen}
        onClose={handleCalendarClose}
      />
    </div>
  );
};

export default AnalysisDateSelect;
